import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { Link, useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import Pagination10 from './components/Pagination10';
import PageFooter from './components/PageFooter';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import usePagination10 from './components/hooks/usePagination10';
import Layout from './components/Layout';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import UnidadeMedidaModal from './components/modalServices/UnidadeMedidaModal';
import Toolbar from './components/Toolbar';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import ResponsiveShadow from './components/ResponsiveShadow';

function UnidadesMedidas() {
  const [UnidadesMedidas, setUnidadesMedidas] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [atualizar, setAtualizar] = useState([]);
  const [selectedUnidadeMedida, setSelectedUnidadeMedida] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUnidadeMedida, setEditingUnidadeMedida] = useState(null);
  const token = localStorage.getItem('token');
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  // Filtrar unidades de medida por descrição
  const normalizeString = str => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  const unidadesMedidasFiltradas = UnidadesMedidas.filter(unidade => {
    const normalizedBusca = normalizeString(filtro);
    return (
      normalizeString(unidade.descricao || '').includes(normalizedBusca) ||
      normalizeString(unidade.descricaoDetalhada || '').includes(
        normalizedBusca
      )
    );
  });

  const totalItems = unidadesMedidasFiltradas.length;
  const { setPagination } = usePagination10(totalItems);

  const selectedUnidadesMedidas = unidadesMedidasFiltradas.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  // Função para gerenciar seleção/desseleção de unidade de medida
  const handleRowClick = unidadeMedida => {
    // Se clicar na mesma unidade já selecionada, desmarca
    if (
      selectedUnidadeMedida &&
      selectedUnidadeMedida.id === unidadeMedida.id
    ) {
      setSelectedUnidadeMedida(null);
    } else {
      setSelectedUnidadeMedida(unidadeMedida);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    setLoading(true);
    axios
      .get(`${API_BASE_URL.UNIDADE_MEDIDA}/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setUnidadesMedidas(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter unidades de medida:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar]);

  // Função para lidar com a exclusão
  function handleDelete(unidadeMedida) {
    showConfirmModal('delete', () => handleConfirmDelete(unidadeMedida));
  }

  function handleConfirmDelete(unidadeMedida) {
    axios
      .delete(`${API_BASE_URL.UNIDADE_MEDIDA}/delete/` + unidadeMedida.id, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        setAtualizar(prev => !prev);
        setSelectedUnidadeMedida(null);
        showDefaultModal('success', 'Unidade de medida excluída com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao excluir unidade de medida:', error);
        showDefaultModal('error', 'Erro ao excluir unidade de medida.');
      });
  }

  // Funções do modal
  function handleOpenModal(unidadeMedida = null) {
    setEditingUnidadeMedida(unidadeMedida);
    setIsModalOpen(true);
  }

  function handleCloseModal() {
    setIsModalOpen(false);
    setEditingUnidadeMedida(null);
  }

  function handleModalSuccess() {
    setAtualizar(prev => !prev);
    handleCloseModal();
  }

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Unidades de Medida" icon="bi-rulers" backButton={false} />
        <PageContent style={{ paddingBottom: 0 }}>
          <Toolbar
            selectedId={selectedUnidadeMedida ? selectedUnidadeMedida.id : null}
            onCreate={() => handleOpenModal()}
            onEdit={(id) => { if (id) handleOpenModal(UnidadesMedidas.find(u => u.id === id)); }}
            onDelete={(id) => { if (id) handleDelete(UnidadesMedidas.find(u => u.id === id)); }}
            onSearch={(v) => setFiltro(v || '')}
            searchValue={filtro}
            searchPlaceholder="Buscar por descrição..."
            searchWidth="250px"
          />

          <LoadingModal loading={isLoading} />

          <TableContainer>
        <table className="table table-hover table-sm align-middle table-custom-radius">
          <TableHeader columns={[
            { label: 'Nº' },
            { label: 'Descrição' },
            { label: 'Descrição Detalhada' },
          ]} />
          <tbody>
            {!isLoading &&
              selectedUnidadesMedidas.map((unidade, index) => {
                const isSelected =
                  selectedUnidadeMedida &&
                  selectedUnidadeMedida.id === unidade.id;
                const numeroLinha = setPagination.startIndex + index + 1;
                return (
                  <tr
                    key={unidade.id}
                    onClick={() => handleRowClick(unidade)}
                    style={{ cursor: 'pointer' }}
                    className={isSelected ? 'table-active' : ''}
                    title={
                      isSelected
                        ? 'Clique novamente para desmarcar'
                        : 'Clique para selecionar'
                    }
                  >
                    <td className={isSelected ? 'fw-bold text-primary' : ''}>
                      {numeroLinha}
                    </td>
                    <td className={isSelected ? 'fw-bold text-primary' : ''}>
                      {unidade.descricao}
                    </td>
                    <td className={isSelected ? 'fw-bold text-primary' : ''}>
                      {unidade.descricaoDetalhada || '-'}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </TableContainer>

      <PageFooter>
        <Pagination10 {...setPagination} />
      </PageFooter>

      <UnidadeMedidaModal
        isOpen={isModalOpen}
        unidadeMedida={editingUnidadeMedida}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
      />

      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
}

export default UnidadesMedidas;
